import * as React from "react";
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  ButtonBase,
  Chip,
  Collapse,
  IconButton,
  List,
  Popover,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import TaskIcon from "@mui/icons-material/Task";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useLocalization } from "@fluent/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import { loadUserTasks } from "./myTasksSlice";
import type { MyTask } from "./myTasksSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectUser } from "../user/userSlice";
import { MiniStatProgress } from "./utils/ApprovePill";
import { TaskListItem } from "./utils/TaskListItem";
import { sortByUrgency } from "./utils/taskUrgency";
import { taskTargetLink } from "./utils/taskRelatedEntity";

// Częstotliwość pobierania z API.
const POLLING_INTERVAL_MS = 60_000;
const MS_PER_DAY = 86_400_000;

type StatusKey = "overdue" | "soon" | "ok" | "none";
type FilterKey = "all" | "overdue" | "soon" | "none";
type Status = {
  key: StatusKey;
  color: "error" | "warning" | "default";
};

// Dotychczasowe zasady klasyfikacji terminów dla filtrów.
export const getStatus = (
  deadlineISO?: string | null,
  now: Date = new Date(),
): Status => {
  if (!deadlineISO) {
    return { key: "none", color: "default" };
  }

  const deadline = new Date(deadlineISO);
  if (Number.isNaN(deadline.getTime())) {
    return { key: "none", color: "default" };
  }

  const diff = Math.ceil((deadline.getTime() - now.getTime()) / MS_PER_DAY);
  if (diff < 0) {
    return { key: "overdue", color: "error" };
  }
  if (diff <= 3) return { key: "soon", color: "warning" };
  return { key: "ok", color: "default" };
};

const iconForTaskType = (taskType: string) => {
  if (taskType === "DOCUMENT_APPROVAL") return <AssignmentTurnedInIcon />;
  if (taskType === "DOCUMENT_OPINION") return <ArticleIcon />;
  return <TaskAltIcon />;
};

export const MyTaskListIcon = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { l10n } = useLocalization();
  const { tokenState } = useAppSelector(selectUser);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [tasks, setTasks] = React.useState<MyTask[]>([]);
  const [filter, setFilter] = React.useState<FilterKey>("all");
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({});
  const [samePageInfoOpen, setSamePageInfoOpen] = React.useState(false);
  const [now, setNow] = React.useState(() => new Date());
  const [loadState, setLoadState] = React.useState<"loading" | "ready" | "error">("loading");

  const open = Boolean(anchorEl);
  const isAuthenticated = tokenState?.tag === "valid";
  const text = (key: string, fallback: string) => l10n.getString(key, null, fallback);

  // Pobieranie danych; kolejny request nie nakłada się na poprzedni.
  React.useEffect(() => {
    if (!isAuthenticated) {
      setTasks([]);
      setAnchorEl(null);
      setLoadState("loading");
      return;
    }

    let active = true;
    let fetching = false;

    const fetchUserTasks = async () => {
      if (fetching) return;
      fetching = true;
      try {
        const userTasks = await dispatch(loadUserTasks()).unwrap();
        if (active) {
          setTasks(userTasks);
          setNow(new Date());
          setLoadState("ready");
        }
      } catch (error) {
        if (active) {
          setLoadState("error");
          console.error("Error fetching tasks:", error);
        }
      } finally {
        fetching = false;
      }
    };

    void fetchUserTasks();
    const timer = window.setInterval(() => void fetchUserTasks(), POLLING_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [dispatch, isAuthenticated]);

  // Wspólny zegar dla filtrów, sortowania i wszystkich TaskListItem.
  React.useEffect(() => {
    if (!open) return;
    setNow(new Date());
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, [open]);

  const counts = React.useMemo(() => {
    const result = { all: tasks.length, overdue: 0, soon: 0, ok: 0, none: 0 };
    tasks.forEach((task) => result[getStatus(task.deadline, now).key]++);
    return result;
  }, [tasks, now]);

  const groups = React.useMemo(() => {
    const filtered = tasks.filter(
      (task) => filter === "all" || getStatus(task.deadline, now).key === filter,
    );
    const grouped = new Map<string, MyTask[]>();
    for (const task of sortByUrgency(filtered, now)) {
      const type = task.taskType || "Other";
      const items = grouped.get(type) ?? [];
      items.push(task);
      grouped.set(type, items);
    }
    return Array.from(grouped.entries());
  }, [tasks, filter, now]);

  const filters: Array<{ key: FilterKey; label: string; color: string; contrast: string }> = [
    { key: "all", label: text("task-filter-all", "Wszystkie"), color: "primary.main", contrast: "primary.contrastText" },
    { key: "overdue", label: text("task-filter-overdue", "Po terminie"), color: "error.main", contrast: "error.contrastText" },
    { key: "soon", label: text("task-filter-soon", "Pilne"), color: "warning.main", contrast: "warning.contrastText" },
    { key: "none", label: text("task-filter-none", "Bez terminu"), color: "text.primary", contrast: "background.paper" },
  ];

  const taskTypeLabel = (type: string) => {
    switch (type) {
      case "DOCUMENT_APPROVAL":
        return text("task-menu-group-approval", "Dokumenty do zatwierdzenia");
      case "DOCUMENT_OPINION":
        return text("task-menu-group-opinion", "Dokumenty do zaopiniowania");
      case "URS_DOCUMENT_TO_PROCESS":
        return text("task-urs-approve", "Zatwierdź dokument URS");
      case "URS_OFFER_RESPONSIBLE_COMMENT":
      case "URS_REQUEST_RESPONSIBLE_COMMENT":
        return text("task-urs-opinion", "Zaopiniuj dokument URS");
      case "Other":
        return text("task-type-generic", "Zadanie");
      default:
        return type;
    }
  };

  const handleOpenTask = (task: MyTask) => {
    const rawLink = taskTargetLink(task);
    if (!rawLink) return;

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const goToPath = (path: string) => {
      setAnchorEl(null);
      if (path === currentPath) {
        setSamePageInfoOpen(true);
      } else {
        navigate(path);
      }
    };

    try {
      const targetUrl = new URL(rawLink, window.location.origin);
      if (targetUrl.origin !== window.location.origin) {
        window.open(targetUrl.href, "_blank", "noopener,noreferrer");
        setAnchorEl(null);
        return;
      }
      goToPath(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
    } catch {
      goToPath(rawLink.startsWith("/") ? rawLink : `/${rawLink}`);
    }
  };

  return (
    <>
      <Tooltip title={text("task-menu-title", "Moje zadania")}>
        <IconButton
          color="inherit"
          aria-label={text("task-menu-open", "Otwórz listę zadań")}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <Badge badgeContent={tasks.length} color="error" max={99}>
            <TaskIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          role: "dialog",
          "aria-label": text("task-menu-title", "Moje zadania"),
          sx: {
            width: 560,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "min(640px, calc(100dvh - 96px))",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1.5, bgcolor: "action.hover", borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}
        >
          <TaskIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {text("task-menu-title", "Moje zadania")}
          </Typography>
          <MiniStatProgress value={tasks.length} color={theme.palette.primary.main} />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={text("task-menu-close", "Zamknij")}>
            <IconButton size="small" aria-label={text("task-menu-close", "Zamknij")} onClick={() => setAnchorEl(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
          {filters.map((item) => {
            const selected = filter === item.key;
            return (
              <Chip
                key={item.key}
                size="small"
                label={`${item.label} (${counts[item.key]})`}
                onClick={() => setFilter(item.key)}
                aria-pressed={selected}
                variant={selected ? "filled" : "outlined"}
                sx={{
                  borderRadius: 1,
                  fontWeight: selected ? 700 : 500,
                  bgcolor: selected ? item.color : "transparent",
                  color: selected ? item.contrast : "text.secondary",
                  borderColor: selected ? item.color : "divider",
                  "&:hover": { bgcolor: selected ? item.color : "action.hover" },
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
          {loadState === "error" && (
            <Alert severity="warning" sx={{ m: 2 }}>
              {text("task-menu-load-error", "Nie udało się odświeżyć zadań. Kolejna próba nastąpi automatycznie.")}
            </Alert>
          )}

          {loadState === "loading" ? (
            <Typography role="status" variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
              {text("task-menu-loading", "Ładowanie zadań…")}
            </Typography>
          ) : groups.length === 0 ? (
            loadState !== "error" && (
              <Stack spacing={0.75} alignItems="center" justifyContent="center" sx={{ minHeight: 200, p: 3, textAlign: "center" }}>
                <CheckCircleOutlineIcon color={filter === "all" ? "success" : "disabled"} sx={{ fontSize: 34 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  {filter === "all"
                    ? text("task-widget-empty-title", "Wszystko na bieżąco")
                    : text("task-menu-filter-empty-title", "Brak zadań dla tego filtra")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filter === "all"
                    ? text("task-widget-empty-subtitle", "Nie masz tutaj zadań do wykonania.")
                    : text("task-menu-filter-empty-subtitle", "Wybierz inny filtr, aby zobaczyć pozostałe zadania.")}
                </Typography>
              </Stack>
            )
          ) : (
            groups.map(([type, items]) => {
              const expanded = openGroups[type] ?? true;
              return (
                <Box key={type} component="section">
                  <ButtonBase
                    onClick={() => setOpenGroups((previous) => ({ ...previous, [type]: !(previous[type] ?? true) }))}
                    aria-expanded={expanded}
                    sx={{
                      width: "100%", textAlign: "left", justifyContent: "flex-start",
                      px: 2, py: 1.25, gap: 1, bgcolor: "action.hover",
                      borderBottom: "1px solid", borderColor: "divider",
                      "&:hover": { bgcolor: "action.selected" },
                      "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
                    }}
                  >
                    <Box sx={{ display: "flex", color: "text.secondary", "& svg": { fontSize: 20 } }}>
                      {iconForTaskType(type)}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                      {taskTypeLabel(type)}
                    </Typography>
                    <MiniStatProgress value={items.length} color={theme.palette.text.secondary} />
                    {expanded ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
                  </ButtonBase>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {items.map((task, index) => (
                        <TaskListItem
                          key={task.id}
                          task={task}
                          now={now}
                          onOpen={handleOpenTask}
                          divider={index < items.length - 1}
                          leftColor="text.primary"
                          rightColor="text.secondary"
                        />
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>

      <Snackbar
        open={samePageInfoOpen}
        autoHideDuration={3200}
        onClose={() => setSamePageInfoOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setSamePageInfoOpen(false)} sx={{ width: "100%", borderRadius: 2 }}>
          <AlertTitle sx={{ mb: 0.25, fontWeight: 600 }}>
            {text("task-same-page-title", "Już jesteś na tym ekranie")}
          </AlertTitle>
          {text("task-same-page-description", "Wybierz inne zadanie, aby przejść dalej.")}
        </Alert>
      </Snackbar>
    </>
  );
};
